// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { ILoggerService } from '@touchplan/logging';
import * as appStrings from '@touchplan/strings';
import {
	IMessageHandlerBaseData,
	MessageHandlerBaseComponent,
	MessageType,
} from '../auth/components/message-handler-base/message-handler-base.component';
import { LoggerServiceFactory } from './LoggerServiceFactory';

/**
 * A service to display a styled snackbar popup at the top of the page.
 *
 * This service's public methods also return a reference to the created
 * MatSnackBar. Any users of this service can tie in existing functionality
 * to the creation or destruction of that MatSnackBar instance as necessary.
 *
 * This service's public methods also expose a parameter to add a "dismiss"
 * button that, when clicked, will hide the snackbar.
 */
@Injectable({ providedIn: 'root' })
export class BannerMessageHandler {
	private defaultLoggerService: ILoggerService = LoggerServiceFactory.create('BannerMessageHandler');
	private config: MatSnackBarConfig = {
		duration: 10000,
		horizontalPosition: 'center',
		verticalPosition: 'top',
		panelClass: ['handle-messages-snack-bar'],
	};

	constructor(private snackBar: MatSnackBar) {}

	public displayErrorMessage<T extends Error & { code?: string }>(
		error: T,
		overwriteErrorMessage?: string,
		action?: string
	): MatSnackBarRef<MessageHandlerBaseComponent> {
		let message = error.message;
		if (overwriteErrorMessage) {
			message = overwriteErrorMessage;
		} else if (!!error.code) {
			message = this.getFirebaseErrorMessageFromCode(error.code);
		}

		this.defaultLoggerService.error(error);
		return this.openMessage(message, MessageType.Error, action);
	}

	public displayInfoMessage(message: string, action?: string): MatSnackBarRef<MessageHandlerBaseComponent> {
		return this.openMessage(message, MessageType.Info, action);
	}

	public displaySuccessMessage(
		message: string,
		action?: string
	): MatSnackBarRef<MessageHandlerBaseComponent> {
		return this.openMessage(message, MessageType.Success, action);
	}

	public displayWarningMessage(
		message: string,
		action?: string
	): MatSnackBarRef<MessageHandlerBaseComponent> {
		return this.openMessage(message, MessageType.Warning, action);
	}

	// additionalErrorInfo param allow us to show more details of a given error like the error stack as example.
	private openMessage(
		message: string,
		messageType: string,
		action?: string,
		additionalErrorInfo?: string
	): MatSnackBarRef<MessageHandlerBaseComponent> {
		return this.snackBar.openFromComponent(MessageHandlerBaseComponent, {
			...this.config,
			panelClass: [...this.config.panelClass, `handle-${messageType}-snack-bar`],
			data: {
				message,
				messageType,
				action,
				error: additionalErrorInfo,
			} as IMessageHandlerBaseData,
		});
	}

	private getFirebaseErrorMessageFromCode(firebaseErrorCode: string) {
		switch (firebaseErrorCode) {
			case 'auth/email-already-exists':
			case 'auth/email-already-in-use':
				return appStrings.BANNER_MESSAGE_EMAIL_ALREADY_EXISTS;
			case 'auth/invalid-argument':
				return appStrings.BANNER_MESSAGE_INVALID_ARGUMENT;
			case 'auth/invalid-password':
				return appStrings.BANNER_MESSAGE_INVALID_PASSWORD;
			case 'auth/phone-number-already-exists':
				return appStrings.BANNER_MESSAGE_PHONE_NUMBER_ALREADY_EXISTS;
			case 'auth/user-not-found':
			case 'auth/invalid-email':
			case 'auth/wrong-password':
				return appStrings.BANNER_MESSAGE_INVALID_USERNAME_OR_PASSWORD;
			case 'auth/password-does-not-meet-requirements':
				return appStrings.BANNER_MESSAGE_PASSWORD_DOES_NOT_MEET_REQUIREMENTS;
			case 'auth/too-many-requests':
				return appStrings.BANNER_MESSAGE_TOO_MANY_AUTH_REQUESTS_ERROR_MESSAGE;
			default:
				return appStrings.BANNER_MESSAGE_DEFAULT_ERROR_MESSAGE;
		}
	}
}
